import type { ButtonHTMLAttributes } from 'react';
import styles from './Button.module.css';

export interface IButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style.
   * - `primary`     — filled navy CTA
   * - `accent`      — filled info-blue CTA, rounder corners: the Scheduler's
   *                   own action look (toolbar Add task, the drawer's step)
   * - `secondary`   — surface fill, navy text, hairline border
   * - `ghost`       — borderless, subtle hover
   * - `danger`      — surface fill, error text + border
   * - `dangerSolid` — filled error CTA
   */
  variant?: 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger' | 'dangerSolid';
  /**
   * Fixed-height layout. `md` = 40px, `sm` = 32px compact. Omit to keep the
   * legacy padding-based sizing (used by ConnectProviderPage).
   */
  size?: 'sm' | 'md';
  /** Stretch to fill the container width. */
  block?: boolean;
}

/**
 * Shared button primitive. `primary` = filled navy CTA, `ghost` = outlined.
 * `secondary` / `danger` / `dangerSolid` plus the `size` / `block` props were
 * promoted from the Project Merge page's hand-rolled button. Passing `size`
 * opts into the fixed-height (32/40px) flex layout; omitting it preserves the
 * original padding-based sizing so existing consumers render unchanged.
 */
export function Button({
  variant = 'primary',
  size,
  block = false,
  type = 'button',
  className,
  ...rest
}: IButtonProps) {
  const cls = [
    styles.button,
    styles[variant],
    size ? styles.sized : '',
    size === 'sm' ? styles.sm : '',
    block ? styles.block : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return <button type={type} className={cls} {...rest} />;
}
